import Slider from 'rc-slider/';
import React, { useState } from 'react';

import SectionTitle from './SectionTitle';
import type { IFilterAttributeRange } from './types';

interface IProps extends IFilterAttributeRange {
  onChange: (urlKey: string, min?: number, max?: number) => void;
}

export default function FilterSectionAttributeRange(props: IProps) {
  const minimum = props.rangeValues.minimumCurrent ?? props.rangeValues.minimum;
  const maximum = props.rangeValues.maximumCurrent ?? props.rangeValues.maximum;

  const marks: Record<number, number> = {};
  props.rangeSteps.forEach((step, idx) => {
    marks[idx] = step;
  });

  const [values, setValues] = useState<[number, number]>([minimum, maximum]);

  const [indices, setIndices] = useState<[number, number]>([
    props.rangeSteps.indexOf(minimum),
    props.rangeSteps.indexOf(maximum),
  ]);

  const sendLower = () => {
    const currentValue = values[0];

    let currentIndex = indices[0];
    if (currentValue === undefined || currentValue < props.rangeValues.minimum) {
      currentIndex = 0;
    } else if (currentValue > maximum) {
      currentIndex = indices[1];
    } else {
      for (let i = 0; i < props.rangeSteps.length; i++) {
        const entry = marks[i];
        if (entry > currentValue) {
          break;
        }

        currentIndex = i;
      }
    }

    const normalized = marks[currentIndex];

    setIndices(state => [currentIndex, state[1]]);
    setValues(state => [normalized, state[1]]);

    onChange(normalized, values[1]);
  };

  const sendUpper = () => {
    const currentValue = values[1];

    let currentIndex = indices[1];
    if (currentValue === undefined || currentValue < props.rangeValues.minimum) {
      currentIndex = props.rangeSteps.length - 1;
    } else if (currentValue > maximum) {
      currentIndex = indices[1];
    } else {
      for (let i = props.rangeSteps.length - 1; i >= 0; i--) {
        const entry = marks[i];
        if (entry < currentValue) {
          break;
        }

        currentIndex = i;
      }
    }

    const normalized = marks[currentIndex];

    setIndices(state => [state[0], currentIndex]);
    setValues(state => [state[0], normalized]);

    onChange(values[0], normalized);
  };

  const onChange = (min?: number, max?: number) => {
    const normalizedMin = min === props.rangeValues.minimum ? undefined : min;
    const normalizedMax = max === props.rangeValues.maximum ? undefined : max;
    if (
      normalizedMin === props.rangeValues.minimumCurrent &&
      normalizedMax === props.rangeValues.maximumCurrent
    ) {
      return;
    }

    props.onChange(props.queryKey, normalizedMin, normalizedMax);
  };

  return (
    <div className="py-2 border-b">
      <SectionTitle title={props.attributeName} rangeUnit={props.rangeUnit} />
      <div>
        <div className="px-2">
          <Slider
            range
            allowCross={false}
            marks={marks}
            step={null}
            min={0}
            max={props.rangeSteps.length - 1}
            value={[indices[0] ?? 0, indices[1] ?? props.rangeSteps.length - 1]}
            defaultValue={[props.rangeValues.minimum, props.rangeValues.maximum]}
            onChange={value => {
              if (!Array.isArray(value)) return;
              setIndices([value[0], value[1]]);
              setValues([marks[value[0]], marks[value[1]]]);
            }}
            onAfterChange={value => {
              if (!Array.isArray(value)) return;
              onChange(marks[value[0]], marks[value[1]]);
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <input
            type="number"
            name="min"
            className="as-text p-2 laptop:p-1 text-sm laptop:text-xs text-center"
            value={values[0] ?? ''}
            onChange={args => {
              const num = args.target.valueAsNumber;
              setValues(state => [num, state[1]]);
            }}
            onKeyDown={args => {
              if (args.key === 'Enter') {
                sendLower();
              }
            }}
            onBlur={() => {
              sendLower();
            }}
          />
          <input
            type="number"
            name="max"
            className="as-text p-2 laptop:p-1 text-sm laptop:text-xs text-center"
            value={values[1] ?? ''}
            onChange={args => {
              const num = args.target.valueAsNumber;
              setValues(state => [state[0], num]);
            }}
            onKeyDown={args => {
              if (args.key === 'Enter') {
                sendUpper();
              }
            }}
            onBlur={() => {
              sendUpper();
            }}
          />
        </div>
      </div>
    </div>
  );
}
